/**
 * Phase 8 T-06c §21.C — Smart Match search history slide-in panel.
 *
 * 360 px panel that slides in from the left. It overlays the record column
 * (intake or canvas) but NOT the global sidebar. Each row surfaces:
 *   - thread name (e.g. `Product Designer`)
 *   - subtitle `N candidates · created {relativeDate}`
 *   - hover-visible `[⋯]` menu → `Rename / Duplicate / Delete`
 *
 * A full-width emerald `[+ New search]` button sits at the top. Clicking a
 * row invokes `onOpenThread(id)` — the parent handles navigation/loading.
 *
 * All CRUD writes go through `smartMatchThreads.ts` (localStorage only,
 * per Phase 8 scope).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal, Plus, X } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  deleteThread,
  duplicateThread,
  readThreads,
  renameThread,
  type SmartMatchThread,
} from '../lib/smartMatchThreads';
import { relativeTimeLabel } from '../lib/recentSearchChip';

const LS_HISTORY_FILTER = 'ggj.smartMatch.history.filter';
export type HistoryListFilter = 'all' | 'mine' | 'shared';

function readHistoryFilter(): HistoryListFilter {
  try {
    const v = localStorage.getItem(LS_HISTORY_FILTER);
    if (v === 'all' || v === 'mine' || v === 'shared') return v;
  } catch {
    /* ignore */
  }
  return 'all';
}

function writeHistoryFilter(f: HistoryListFilter): void {
  try {
    localStorage.setItem(LS_HISTORY_FILTER, f);
  } catch {
    /* ignore */
  }
}

interface HistorySlideInProps {
  open: boolean;
  onClose: () => void;
  onOpenThread: (threadId: string) => void;
  onNewSearch: () => void;
  /** Optional — used by the canvas to light up the currently-open thread. */
  activeThreadId?: string | null;
}

function newDuplicateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function HistorySlideIn({
  open,
  onClose,
  onOpenThread,
  onNewSearch,
  activeThreadId = null,
}: HistorySlideInProps) {
  const { user } = useAuth();
  const [threads, setThreads] = useState<SmartMatchThread[]>([]);
  const [historyFilter, setHistoryFilter] = useState<HistoryListFilter>('all');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(() => {
    setThreads(readThreads());
  }, []);

  useEffect(() => {
    if (!open) return;
    setHistoryFilter(readHistoryFilter());
    refresh();
    const onChange = () => refresh();
    window.addEventListener('ggj:smartmatch-threads-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('ggj:smartmatch-threads-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (renamingId) {
          setRenamingId(null);
          return;
        }
        if (menuOpenFor) {
          setMenuOpenFor(null);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [menuOpenFor, onClose, open, renamingId]);

  useEffect(() => {
    if (!menuOpenFor) return;
    const onDocClick = (e: MouseEvent) => {
      if (!panelRef.current) return;
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-ggj-row-menu]')) return;
      setMenuOpenFor(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpenFor]);

  const sorted = useMemo(() => threads, [threads]);

  const filteredThreads = useMemo(() => {
    const uid = user?.id ?? null;
    if (historyFilter === 'all') return sorted;
    if (!uid) {
      return historyFilter === 'mine' ? sorted : [];
    }
    if (historyFilter === 'mine') {
      return sorted.filter((t) => !t.createdByUserId || t.createdByUserId === uid);
    }
    return sorted.filter((t) => Boolean(t.createdByUserId) && t.createdByUserId !== uid);
  }, [sorted, historyFilter, user?.id]);

  const setFilter = (f: HistoryListFilter) => {
    setHistoryFilter(f);
    writeHistoryFilter(f);
  };

  const now = Date.now();

  const startRename = (t: SmartMatchThread) => {
    setRenamingId(t.id);
    setRenameValue(t.name);
    setMenuOpenFor(null);
  };
  const commitRename = () => {
    if (!renamingId) return;
    renameThread(renamingId, renameValue);
    setRenamingId(null);
    refresh();
  };
  const doDuplicate = (t: SmartMatchThread) => {
    duplicateThread(t.id, newDuplicateId());
    setMenuOpenFor(null);
    refresh();
  };
  const doDelete = (t: SmartMatchThread) => {
    deleteThread(t.id);
    setMenuOpenFor(null);
    refresh();
  };

  if (!open) return null;

  return (
    <>
      {/* Dimmed backdrop over the current route — not the global sidebar. */}
      <button
        type="button"
        aria-label="Close search history"
        className="absolute inset-0 z-[40] bg-black/20"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-label="Search history"
        className="absolute inset-y-0 left-0 z-[41] flex w-[360px] flex-col border-r shadow-xl"
        style={{
          background: 'var(--vscode-bg-primary)',
          borderColor: 'var(--vscode-border-primary)',
        }}
      >
        <div
          className="flex items-start justify-between gap-3 border-b px-4 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div className="min-w-0 flex-1">
            <h2
              className="text-[14px] font-semibold leading-tight"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              Search history
            </h2>
            <p
              className="mt-0.5 whitespace-nowrap text-[11px] leading-[1.3]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {threads.length} search{threads.length === 1 ? '' : 'es'} · last 30 days
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-[color:var(--vscode-text-tertiary)] hover:text-[color:var(--vscode-text-primary)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-3 pt-3">
          <button
            type="button"
            onClick={onNewSearch}
            className="flex h-9 w-full items-center justify-center gap-1.5 rounded-[10px] bg-[var(--ggj-accent,#10B981)] px-3 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New search
          </button>
          <div
            className="mt-2 flex rounded-[8px] border p-0.5"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
            role="group"
            aria-label="Filter history"
          >
            {(
              [
                { id: 'all' as const, label: 'All' },
                { id: 'mine' as const, label: 'Mine' },
                { id: 'shared' as const, label: 'Shared' },
              ] as const
            ).map((opt) => {
              const active = historyFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFilter(opt.id)}
                  className="flex-1 rounded-[6px] px-2 py-1 text-[11px] font-medium transition-colors"
                  style={{
                    background: active ? 'var(--vscode-bg-elevated)' : 'transparent',
                    color: active ? 'var(--vscode-text-primary)' : 'var(--vscode-text-secondary)',
                    boxShadow: active ? '0 0 0 1px color-mix(in srgb, var(--ggj-accent,#10B981) 40%, transparent)' : undefined,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4">
          {filteredThreads.length === 0 ? (
            <p
              className="px-3 py-6 text-center text-[12px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {sorted.length === 0
                ? 'No searches yet. Start one above.'
                : 'No searches match this filter.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {filteredThreads.map((t) => {
                const active = activeThreadId === t.id;
                const subtitle = `${t.candidateCount} candidate${
                  t.candidateCount === 1 ? '' : 's'
                } · created ${relativeTimeLabel(t.createdAt, now)}`;
                return (
                  <li key={t.id} className="group relative">
                    {renamingId === t.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        className="h-8 w-full rounded-[8px] border bg-[var(--vscode-bg-elevated)] px-2 text-[13px] font-medium outline-none"
                        style={{
                          borderColor: 'var(--ggj-accent,#10B981)',
                          color: 'var(--vscode-text-primary)',
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onOpenThread(t.id)}
                        className={`flex w-full items-center gap-2 rounded-[8px] px-2 py-1.5 text-left transition-colors ${
                          active
                            ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]'
                            : 'hover:bg-[var(--vscode-bg-hover)]'
                        }`}
                      >
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{
                            background: active
                              ? 'var(--ggj-accent,#10B981)'
                              : 'var(--vscode-text-tertiary)',
                            opacity: active ? 1 : 0.45,
                          }}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span
                            className="block truncate text-[13px] font-medium"
                            style={{
                              color: active
                                ? '#065F46'
                                : 'var(--vscode-text-primary)',
                            }}
                          >
                            {t.name}
                          </span>
                          <span
                            className="block truncate text-[11px]"
                            style={{ color: 'var(--vscode-text-tertiary)' }}
                          >
                            {subtitle}
                          </span>
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      data-ggj-row-menu
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenFor((prev) => (prev === t.id ? null : t.id));
                      }}
                      aria-label={`Actions for ${t.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menuOpenFor === t.id}
                      className="absolute right-1.5 top-1.5 rounded p-1 text-[color:var(--vscode-text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                    </button>

                    {menuOpenFor === t.id ? (
                      <div
                        data-ggj-row-menu
                        role="menu"
                        className="absolute right-1.5 top-8 z-[42] w-[160px] overflow-hidden rounded-[10px] border bg-[var(--vscode-bg-elevated)] py-1 shadow-lg"
                        style={{ borderColor: 'var(--vscode-border-primary)' }}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => startRename(t)}
                          className="block w-full px-3 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                          style={{ color: 'var(--vscode-text-primary)' }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => doDuplicate(t)}
                          className="block w-full px-3 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                          style={{ color: 'var(--vscode-text-primary)' }}
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => doDelete(t)}
                          className="block w-full px-3 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                          style={{ color: '#DC2626' }}
                        >
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}
